// src/screens/TerritoriesMapScreen.js
import React, { useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import AccountsMap from "../components/map/AccountsMap";
import SurfaceState from "../components/common/SurfaceState";

const TerritoriesMapScreen = () => {
  const auth = useMemo(() => getAuth(), []);
  const db = useMemo(() => getFirestore(), []);

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      setLoading(true);
      setError("");

      if (!user) {
        setFirebaseUser(null);
        setCompanyId(null);
        setLoading(false);
        return;
      }

      setFirebaseUser(user);

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!snap.exists()) {
          setError("User profile not found.");
          setCompanyId(null);
        } else {
          const data = snap.data();
          const resolvedCompanyId = data.companyId;

          if (!resolvedCompanyId) {
            console.error(
              "[TerritoriesMapScreen] Missing users/{uid}.companyId for:",
              user.uid
            );
            setError("Your user profile is missing companyId. Please contact admin.");
            setCompanyId(null);
          } else {
            setCompanyId(resolvedCompanyId);
          }
        }
      } catch (err) {
        console.error("[TerritoriesMapScreen] Error loading user/company:", err);
        setError("Failed to load company information.");
        setCompanyId(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [auth, db]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="loading"
            title="Loading map"
            description="Bringing in your company, territories, and account coverage now."
          />
        </div>
      </div>
    );
  }

  if (!firebaseUser || !companyId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="error"
            title="Map unavailable"
            description={error || "Could not load company information. Please sign in again."}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none mx-0 px-0 py-1">
      {error && (
        <SurfaceState
          tone="error"
          compact
          className="mb-3"
          title="Map data issue"
          description={error}
        />
      )}

      <AccountsMap companyId={companyId} initialTab="territories" variant="territories" />
    </div>
  );
};

export default TerritoriesMapScreen;
